import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { normaliseError } from '@/api/baseQuery';
import { useGetAccountQuery, useUpdateAccountMutation } from '@/api/generated/accountApi';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { displayNameChanged, selectCurrentUser } from '@/features/auth/authSlice';

/**
 * Asks a new vendor for their business name, once. Customers see it in every order chat, so
 * the placeholder a new account gets must not reach them.
 */
export function BusinessNameDialog() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const account = useGetAccountQuery(undefined, { skip: !user });
  const [update, updateState] = useUpdateAccountMutation();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!account.data?.needsName) {
    return null;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Enter your business name');
      return;
    }
    try {
      const saved = await update({ updateAccountForm: { displayName: name.trim() } }).unwrap();
      dispatch(displayNameChanged(saved.displayName ?? name.trim()));
    } catch (err) {
      setError(normaliseError(err).message);
    }
  }

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>What is your business called?</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customers see this name in their order chats and in the Slack messages you send.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Business name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Sharma Hardware"
            autoFocus
            fullWidth
            required
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button type="submit" variant="contained" disabled={updateState.isLoading}>
            {updateState.isLoading ? 'Saving…' : 'Continue'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
